/**
 * Marketplace Service for Function Discovery, Rating, and Commerce
 * Implements comprehensive function marketplace operations
 */

import { apiService, ApiResponse } from './api';

export interface FunctionListing {
  id: string;
  rufid: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  tags: string[];
  version: string;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatar?: string;
    verified: boolean;
  };
  pricing: {
    model: 'free' | 'paid' | 'subscription' | 'usage';
    price?: number;
    currency: string;
    billingCycle?: 'monthly' | 'annual';
    freeLimit?: number;
    usagePrice?: number;
  };
  stats: {
    downloads: number;
    rating: number;
    reviewCount: number;
    usageCount: number;
    lastUpdated: string;
  };
  metadata: {
    readme: string;
    documentation?: string;
    examples: string[];
    requirements: string[];
    compatibility: string[];
  };
  status: 'published' | 'draft' | 'archived' | 'rejected';
  featured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FunctionCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  subcategories: {
    id: string;
    name: string;
    description: string;
    count: number;
  }[];
  count: number;
}

export interface FunctionReview {
  id: string;
  functionId: string;
  userId: string;
  userDisplayName: string;
  userAvatar?: string;
  rating: number;
  title: string;
  content: string;
  helpful: number;
  createdAt: string;
  updatedAt: string;
}

export interface SearchFilters {
  category?: string;
  subcategory?: string;
  tags?: string[];
  pricing?: 'free' | 'paid' | 'subscription' | 'usage';
  minRating?: number;
  author?: string;
  featured?: boolean;
  compatibility?: string[];
}

export interface SearchParams {
  query?: string;
  filters?: SearchFilters;
  sortBy?: 'relevance' | 'popularity' | 'rating' | 'newest' | 'updated';
  page?: number;
  limit?: number;
}

export interface SearchResult {
  functions: FunctionListing[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  aggregations: {
    categories: { [key: string]: number };
    tags: { [key: string]: number };
    pricingModels: { [key: string]: number };
  };
}

export interface PurchaseRequest {
  functionId: string;
  pricingTier?: string;
  paymentMethodId?: string;
}

export interface PurchaseResponse {
  transactionId: string;
  functionId: string;
  accessGranted: boolean;
  downloadUrl?: string;
  accessKey?: string;
  expiresAt?: string;
}

export interface MarketplaceStats {
  totalFunctions: number;
  activeFunctions: number;
  totalSellers: number;
  totalBuyers: number;
  monthlyTransactions: number;
  monthlyRevenue: number;
  averageFunctionPrice: number;
  topCategories: { category: string; count: number }[];
}

export interface MarketplaceOverview {
  stats: MarketplaceStats;
  categories: FunctionCategory[];
}

const CATEGORY_METADATA: Record<string, { name: string; description: string; icon: string }> = {
  ai_ml: {
    name: 'AI & Machine Learning',
    description: 'Models, inference endpoints, and feature pipelines ready to embed.',
    icon: '🧠',
  },
  data_processing: {
    name: 'Data Processing',
    description: 'ETL jobs, enrichment utilities, and data quality automation.',
    icon: '🗃️',
  },
  api_integration: {
    name: 'API Integrations',
    description: 'Prebuilt connectors for SaaS platforms, CRMs, and developer tooling.',
    icon: '🔌',
  },
  automation: {
    name: 'Automation',
    description: 'Workflow orchestration, scheduling, and rule engines for ops teams.',
    icon: '🤖',
  },
  analytics: {
    name: 'Analytics & Insights',
    description: 'Dashboards, telemetry aggregation, and experimentation helpers.',
    icon: '📈',
  },
  communication: {
    name: 'Communication',
    description: 'Email, chat, and notification routines with compliance baked in.',
    icon: '💬',
  },
  security: {
    name: 'Security',
    description: 'Policy enforcement, audit logging, and threat detection utilities.',
    icon: '🛡️',
  },
  utilities: {
    name: 'Utilities',
    description: 'Helper functions, formatting, and developer productivity primitives.',
    icon: '🧰',
  },
};

const DEFAULT_CATEGORY_META = {
  name: 'General Functions',
  description: 'Reusable building blocks contributed by the Relay developer community.',
  icon: '✨',
};

interface CommerceSearchResponse {
  results: any[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  aggregations?: {
    categories?: Record<string, number>;
    tags?: Record<string, number>;
    pricing_models?: Record<string, number>;
  };
}

class MarketplaceService {
  private readonly basePath = '/commerce';

  private buildSearchPayload(params: SearchParams = {}): Record<string, any> {
    const body: Record<string, any> = {
      query: params.query,
      sort_by: params.sortBy ?? 'relevance',
    };

    const filters = params.filters ?? {};

    if (filters.category) body.category = filters.category;
    if (filters.pricing) body.pricing_model = filters.pricing;
    if (filters.minRating) body.min_rating = filters.minRating;
    if (filters.featured) body.featured_only = true;
    if (filters.tags?.length) body.tags = filters.tags;

    return body;
  }

  private normalizeListing(raw: any): FunctionListing {
    const pricingModel = (raw?.pricing_model ?? raw?.pricing?.model ?? 'usage') as
      | 'free'
      | 'paid'
      | 'subscription'
      | 'usage';

    const usagePrice = Number(raw?.usage_price ?? raw?.pricing?.usagePrice ?? 0) || undefined;
    const basePrice = Number(raw?.base_price ?? raw?.pricing?.price ?? 0) || undefined;

    const currency = (raw?.pricing?.currency ?? 'usd').toLowerCase();

    const pricing = (() => {
      switch (pricingModel) {
        case 'free':
          return { model: 'free' as const, currency };
        case 'paid':
          return { model: 'paid' as const, price: basePrice, currency };
        case 'subscription':
          return {
            model: 'subscription' as const,
            price: basePrice,
            currency,
            billingCycle: raw?.pricing?.billingCycle ?? 'monthly',
          };
        default:
          return {
            model: 'usage' as const,
            usagePrice,
            currency,
            freeLimit: raw?.pricing?.freeLimit,
          };
      }
    })();

    const rating = Number(raw?.rating ?? raw?.stats?.rating ?? 0) || 0;
    const reviewCount = Number(raw?.total_reviews ?? raw?.stats?.reviewCount ?? 0) || 0;
    const usageCount = Number(
      raw?.monthly_executions ?? raw?.stats?.usageCount ?? raw?.stats?.usage ?? 0,
    ) || 0;

    const downloads = Number(raw?.metadata?.downloads ?? raw?.stats?.downloads ?? usageCount) || 0;

    const authorMetadata = raw?.metadata?.author ?? {};

    return {
      id:
        raw?.id ??
        raw?.functionId ??
        raw?.listing_id ??
        `listing-${Math.random().toString(36).slice(2)}`,
      rufid: raw?.rufid ?? raw?.metadata?.rufid ?? 'rufid:unknown',
      name: raw?.name ?? raw?.title ?? raw?.function_name ?? 'Untitled function',
      description: raw?.description ?? raw?.metadata?.description ?? 'No description provided.',
      category: raw?.category ?? raw?.metadata?.category ?? 'utilities',
      subcategory: raw?.subcategory ?? raw?.metadata?.subcategory,
      tags: Array.isArray(raw?.tags) ? raw.tags : Array.isArray(raw?.metadata?.tags) ? raw.metadata.tags : [],
      version: raw?.version ?? raw?.metadata?.version ?? '1.0.0',
      author: {
        id: raw?.seller_id ?? authorMetadata?.id ?? 'relay-author',
        username: authorMetadata?.username ?? 'relay',
        displayName: authorMetadata?.displayName ?? raw?.metadata?.seller_display_name ?? 'Relay Labs',
        avatar: authorMetadata?.avatar ?? raw?.metadata?.seller_avatar,
        verified: Boolean(authorMetadata?.verified ?? raw?.metadata?.seller_verified ?? false),
      },
      pricing,
      stats: {
        downloads,
        rating,
        reviewCount,
        usageCount,
        lastUpdated: raw?.updated_at ?? raw?.updatedAt ?? new Date().toISOString(),
      },
      metadata: {
        readme: raw?.metadata?.readme ?? '# Function overview\nDocumentation coming soon.',
        documentation: raw?.metadata?.documentation,
        examples: Array.isArray(raw?.metadata?.examples) ? raw.metadata.examples : [],
        requirements: Array.isArray(raw?.metadata?.requirements) ? raw.metadata.requirements : [],
        compatibility: Array.isArray(raw?.metadata?.compatibility) ? raw.metadata.compatibility : [],
      },
      status: (raw?.status ?? raw?.validation_status ?? 'published') as
        | 'published'
        | 'draft'
        | 'archived'
        | 'rejected',
      featured: Boolean(raw?.featured ?? raw?.metadata?.featured),
      createdAt: raw?.created_at ?? raw?.createdAt ?? new Date().toISOString(),
      updatedAt: raw?.updated_at ?? raw?.updatedAt ?? new Date().toISOString(),
    };
  }

  private mapCategoriesFromStats(stats: MarketplaceStats | null): FunctionCategory[] {
    if (!stats) return [];

    return stats.topCategories.map(({ category, count }) => {
      const meta = CATEGORY_METADATA[category] ?? DEFAULT_CATEGORY_META;
      return {
        id: category,
        name: meta.name,
        description: meta.description,
        icon: meta.icon,
        subcategories: [],
        count,
      };
    });
  }

  // Function Discovery
  async searchFunctions(params: SearchParams = {}): Promise<ApiResponse<SearchResult>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    const payload = this.buildSearchPayload(params);

    const response = await apiService.post<CommerceSearchResponse>(
      `${this.basePath}/functions/search?page=${page}&page_size=${limit}`,
      payload,
    );

    if (response.status === 'error') {
      return {
        status: 'error',
        error:
          response.error ?? {
            status: 500,
            message: 'Marketplace search failed',
            timestamp: new Date().toISOString(),
          },
      };
    }

    if (!response.data) {
      return {
        status: 'success',
        data: {
          functions: [],
          total: 0,
          page,
          limit,
          hasMore: false,
          aggregations: {
            categories: {},
            tags: {},
            pricingModels: {},
          },
        },
      };
    }

    const { results = [], pagination, aggregations } = response.data;

    const functions = Array.isArray(results) ? results.map((item) => this.normalizeListing(item)) : [];
    const total = pagination?.total_count ?? functions.length;
    const currentPage = pagination?.page ?? page;
    const pageSize = pagination?.page_size ?? limit;
    const totalPages = pagination?.total_pages ?? Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
    const hasMore = currentPage < totalPages;

    const normalizeAggregation = (input?: Record<string, number>): Record<string, number> =>
      input ? { ...input } : {};

    const derivedAggregations = {
      categories: normalizeAggregation(aggregations?.categories),
      tags: normalizeAggregation(aggregations?.tags),
      pricingModels: normalizeAggregation(aggregations?.pricing_models),
    };

    if (!aggregations) {
      const categoryCounts: Record<string, number> = {};
      const tagCounts: Record<string, number> = {};
      const pricingCounts: Record<string, number> = {};

      functions.forEach((func) => {
        categoryCounts[func.category] = (categoryCounts[func.category] ?? 0) + 1;
        func.tags.forEach((tag) => {
          tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
        });
        pricingCounts[func.pricing.model] = (pricingCounts[func.pricing.model] ?? 0) + 1;
      });

      derivedAggregations.categories = categoryCounts;
      derivedAggregations.tags = tagCounts;
      derivedAggregations.pricingModels = pricingCounts;
    }

    return {
      status: 'success',
      data: {
        functions,
        total,
        page: currentPage,
        limit: pageSize,
        hasMore,
        aggregations: derivedAggregations,
      },
    };
  }

  async getFunctionDetails(functionId: string): Promise<ApiResponse<FunctionListing>> {
    // Details endpoint is not yet available on the managed API; emulate via search fallback.
    const response = await this.searchFunctions({ filters: {}, limit: 1, query: functionId });
    if (response.status === 'success' && response.data?.functions.length) {
      return { status: 'success', data: response.data.functions[0] };
    }
    return apiService.get(`${this.basePath}/functions/${functionId}`);
  }

  async getFeaturedFunctions(limit: number = 20): Promise<ApiResponse<FunctionListing[]>> {
    const response = await this.searchFunctions({ filters: { featured: true }, limit });
    if (response.status === 'success' && response.data) {
      return { status: 'success', data: response.data.functions.slice(0, limit) };
    }
    return { status: response.status, error: response.error } as ApiResponse<FunctionListing[]>;
  }

  async getPopularFunctions(limit: number = 20): Promise<ApiResponse<FunctionListing[]>> {
    const response = await this.searchFunctions({ sortBy: 'popularity', limit });
    if (response.status === 'success' && response.data) {
      return { status: 'success', data: response.data.functions.slice(0, limit) };
    }
    return { status: response.status, error: response.error } as ApiResponse<FunctionListing[]>;
  }

  async getRecentFunctions(limit: number = 20): Promise<ApiResponse<FunctionListing[]>> {
    const response = await this.searchFunctions({ sortBy: 'newest', limit });
    if (response.status === 'success' && response.data) {
      return { status: 'success', data: response.data.functions.slice(0, limit) };
    }
    return { status: response.status, error: response.error } as ApiResponse<FunctionListing[]>;
  }

  // Categories & Marketplace stats
  async getMarketplaceStats(): Promise<ApiResponse<MarketplaceStats>> {
    const response = await apiService.get(`${this.basePath}/stats`);

    if (response.status !== 'success' || !response.data) {
      return response as ApiResponse<MarketplaceStats>;
    }

    const raw = response.data as any;
    const toNumber = (value: unknown): number => {
      if (value === null || value === undefined) return 0;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const stats: MarketplaceStats = {
      totalFunctions: toNumber(raw.total_functions ?? raw.totalFunctions),
      activeFunctions: toNumber(raw.active_functions ?? raw.activeFunctions),
      totalSellers: toNumber(raw.total_sellers ?? raw.totalSellers),
      totalBuyers: toNumber(raw.total_buyers ?? raw.totalBuyers),
      monthlyTransactions: toNumber(raw.monthly_transactions ?? raw.monthlyTransactions),
      monthlyRevenue: toNumber(raw.monthly_revenue ?? raw.monthlyRevenue),
      averageFunctionPrice: toNumber(raw.average_function_price ?? raw.averageFunctionPrice),
      topCategories: Array.isArray(raw.top_categories ?? raw.topCategories)
        ? (raw.top_categories ?? raw.topCategories).map((entry: any) => ({
            category: entry.category ?? entry.id ?? 'utilities',
            count: toNumber(entry.count ?? entry.value),
          }))
        : [],
    };

    return { status: 'success', data: stats };
  }

  async getMarketplaceOverview(): Promise<ApiResponse<MarketplaceOverview>> {
    const statsResponse = await this.getMarketplaceStats();
    if (statsResponse.status !== 'success' || !statsResponse.data) {
      return { status: statsResponse.status, error: statsResponse.error } as ApiResponse<MarketplaceOverview>;
    }

    const categories = this.mapCategoriesFromStats(statsResponse.data);
    return {
      status: 'success',
      data: {
        stats: statsResponse.data,
        categories,
      },
    };
  }

  async getCategories(): Promise<ApiResponse<FunctionCategory[]>> {
    const overview = await this.getMarketplaceOverview();
    if (overview.status === 'success' && overview.data) {
      return { status: 'success', data: overview.data.categories };
    }
    return { status: overview.status, error: overview.error } as ApiResponse<FunctionCategory[]>;
  }

  async getCategoryFunctions(
    categoryId: string,
    params: SearchParams = {},
  ): Promise<ApiResponse<SearchResult>> {
    const mergedFilters = { ...(params.filters ?? {}), category: categoryId };
    return this.searchFunctions({ ...params, filters: mergedFilters });
  }

  // Reviews and Ratings
  async getFunctionReviews(functionId: string, page: number = 1, limit: number = 20): Promise<ApiResponse<{
    reviews: FunctionReview[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/reviews?page=${page}&limit=${limit}`);
  }

  async submitReview(functionId: string, review: {
    rating: number;
    title: string;
    content: string;
  }): Promise<ApiResponse<FunctionReview>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/reviews`, review);
  }

  async updateReview(functionId: string, reviewId: string, update: {
    rating?: number;
    title?: string;
    content?: string;
  }): Promise<ApiResponse<FunctionReview>> {
    return apiService.patch(`${this.basePath}/functions/${functionId}/reviews/${reviewId}`, update);
  }

  async markReviewHelpful(functionId: string, reviewId: string, helpful: boolean): Promise<ApiResponse<void>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/reviews/${reviewId}/helpful`, { helpful });
  }

  // Commerce Operations
  async purchaseFunction(request: PurchaseRequest): Promise<ApiResponse<PurchaseResponse>> {
    return apiService.post(`${this.basePath}/purchase`, request);
  }

  async getPurchasedFunctions(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    functions: (FunctionListing & { purchasedAt: string; accessKey: string })[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    return apiService.get(`${this.basePath}/purchased?page=${page}&limit=${limit}`);
  }

  async downloadFunction(functionId: string): Promise<ApiResponse<{
    downloadUrl: string;
    expiresAt: string;
  }>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/download`);
  }

  // Function Testing
  async testFunction(functionId: string, testData: any): Promise<ApiResponse<{
    result: any;
    executionTime: number;
    success: boolean;
    error?: string;
  }>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/test`, { data: testData });
  }

  async getFunctionSchema(functionId: string): Promise<ApiResponse<{
    input: any;
    output: any;
    examples: any[];
  }>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/schema`);
  }

  // User Preferences
  async addToWishlist(functionId: string): Promise<ApiResponse<void>> {
    const response = await apiService.post<void>(`${this.basePath}/wishlist`, { functionId });
    if (response.status === 'error' && response.error?.status === 404) {
      // Wishlist endpoints are not yet deployed; pretend success so UI remains optimistic.
      return { status: 'success' };
    }
    return response;
  }

  async removeFromWishlist(functionId: string): Promise<ApiResponse<void>> {
    const response = await apiService.delete<void>(`${this.basePath}/wishlist/${functionId}`);
    if (response.status === 'error' && response.error?.status === 404) {
      return { status: 'success' };
    }
    return response;
  }

  async getWishlist(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    functions: FunctionListing[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    const response = await apiService.get<{
      functions?: any[];
      total?: number;
      page?: number;
      limit?: number;
      hasMore?: boolean;
    }>(`${this.basePath}/wishlist?page=${page}&limit=${limit}`);
    if (response.status === 'error' && response.error?.status === 404) {
      return {
        status: 'success',
        data: {
          functions: [],
          total: 0,
          page,
          limit,
          hasMore: false,
        },
      };
    }
    if (response.status === 'success' && response.data) {
      const functions = Array.isArray(response.data.functions)
        ? response.data.functions.map((item: any) => this.normalizeListing(item))
        : [];
      return {
        status: 'success',
        data: {
          functions,
          total: response.data.total ?? functions.length,
          page: response.data.page ?? page,
          limit: response.data.limit ?? limit,
          hasMore: Boolean(response.data.hasMore ?? false),
        },
      };
    }
    return response as ApiResponse<{
      functions: FunctionListing[];
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
    }>;
  }

  // Analytics for Users
  async getFunctionUsageStats(functionId: string, period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<ApiResponse<{
    totalCalls: number;
    successRate: number;
    avgResponseTime: number;
    usageByDay: { date: string; calls: number }[];
  }>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/stats?period=${period}`);
  }
}

export const marketplaceService = new MarketplaceService();
export default marketplaceService;
